import React from 'react';

interface LogoProps {
  variant?: 'primary' | 'horizontal' | 'icon' | 'dark' | 'monochrome' | 'reverse' | 'nav';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showText?: boolean;
}

const sizeClasses = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-12',
  xl: 'h-16',
  '2xl': 'h-20',
};

const Logo: React.FC<LogoProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  className = '',
  showText = true 
}) => {
  const logoPath = `/logo-${variant}.svg`;
  const sizeClass = sizeClasses[size];
  
  // 如果是图标版本，不显示文字
  const isIconOnly = variant === 'icon' || !showText;
  
  return (
    <div className={`flex items-center ${className}`}>
      <img 
        src={logoPath}
        alt={`LeapWard ${variant === 'icon' ? '图标' : 'Logo'} - 思维飞跃训练平台`}
        className={`${sizeClass} w-auto`}
        loading="lazy"
        role="img"
        aria-label={`LeapWard品牌标识，${variant === 'icon' ? '图标版本' : '完整版本'}`}
        onError={(e) => {
          // 如果SVG加载失败，回退到默认图标
          const target = e.target as HTMLImageElement;
          target.src = '/pwa-192x192.png';
          target.alt = 'LeapWard 默认图标';
        }}
      />
      {!isIconOnly && variant !== 'horizontal' && (
        <div className="ml-2">
          <span className="text-lg font-bold text-blue-800 dark:text-blue-200">
            Leap<span className="text-blue-600 dark:text-blue-300">Ward</span>
          </span>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            思维飞跃
          </div>
        </div>
      )}
    </div>
  );
};

// 预设的Logo组合
export const LogoWithText: React.FC<Omit<LogoProps, 'showText'>> = (props) => (
  <Logo {...props} showText={true} />
);

export const LogoIcon: React.FC<Omit<LogoProps, 'showText' | 'variant'>> = (props) => (
  <Logo {...props} variant="icon" showText={false} />
);

export const LogoHorizontal: React.FC<Omit<LogoProps, 'showText' | 'variant'>> = (props) => (
  <Logo {...props} variant="horizontal" showText={false} />
);

export const LogoNav: React.FC<Omit<LogoProps, 'showText' | 'variant'>> = (props) => (
  <Logo {...props} variant="nav" showText={false} />
);

// 响应式Logo组件 - 优化版本，确保LeapWard品牌突出
export const ResponsiveLogo: React.FC<Omit<LogoProps, 'variant'>> = (props) => {
  return (
    <div className="flex items-center">
      {/* 移动端显示图标版本 */}
      <div className="block sm:hidden">
        <Logo {...props} variant="icon" showText={false} size="lg" />
      </div>
      {/* 桌面端显示导航栏专用版本，品牌名称更突出 */}
      <div className="hidden sm:block">
        <Logo {...props} variant="nav" showText={false} size="xl" />
      </div>
    </div>
  );
};

// 主题感知Logo组件
export const ThemeAwareLogo: React.FC<Omit<LogoProps, 'variant'>> = (props) => {
  return (
    <>
      {/* 浅色主题 */}
      <div className="block dark:hidden">
        <Logo {...props} variant="primary" />
      </div>
      {/* 深色主题 */}
      <div className="hidden dark:block">
        <Logo {...props} variant="dark" />
      </div>
    </>
  );
};

export default Logo;